import {
	Arrow,
	Portal,
	Provider,
	Content as RadixTooltipContent,
	type TooltipProps as RadixTooltipProps,
	Root,
	type TooltipContentProps,
	type TooltipPortalProps,
	type TooltipTriggerProps,
	Trigger
} from '@radix-ui/react-tooltip'
import {
	type CSSProperties,
	forwardRef,
	type PropsWithChildren,
	type ReactNode,
	useState
} from 'react'
import { cn } from '@/helpers/utils'

interface TooltipProps extends PropsWithChildren {
	children: ReactNode
	content: ReactNode
	contentProps?: TooltipContentProps
	delayDuration?: number
	portalProps?: TooltipPortalProps
	rootProps?: RadixTooltipProps
	triggerProps?: TooltipTriggerProps
	variant?: 'light' | 'dark'
	maxWidth?: CSSProperties['maxWidth']
	isOpenOnClickEnabled?: boolean
}

const variantStyles = {
	light: 'bg-neutral-white text-neutral-700 [&_svg]:fill-neutral-white',
	dark: 'bg-neutral-900 text-neutral-white [&_svg]:fill-neutral-900'
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
	(
		{
			children,
			rootProps,
			triggerProps,
			portalProps,
			contentProps,
			content,
			variant = 'dark',
			delayDuration = 350,
			maxWidth,
			isOpenOnClickEnabled = true
		},
		ref
	) => {
		const [isForcedOpen, setIsForcedOpen] = useState(false)

		return (
			<Provider delayDuration={delayDuration}>
				<Root
					open={(isOpenOnClickEnabled && isForcedOpen) || undefined}
					{...rootProps}
				>
					<Trigger
						{...triggerProps}
						onClick={(...args) => {
							setIsForcedOpen(true)
							triggerProps?.onClick?.(...args)
						}}
					>
						{children}
					</Trigger>
					<Portal {...portalProps}>
						<RadixTooltipContent
							className={cn(
								'z-(--z-tooltip) flex flex-col items-start self-stretch break-words rounded-lg px-3 py-2 font-semibold text-xs shadow-lg [&_u]:underline',
								variantStyles[variant]
							)}
							collisionPadding={8}
							onPointerDownOutside={(...args) => {
								setIsForcedOpen(false)
								contentProps?.onPointerDownOutside?.(...args)
							}}
							ref={ref}
							style={{ maxWidth: maxWidth ?? '90vw' }}
							{...contentProps}
						>
							{content}
							<Arrow height={6} width={16} />
						</RadixTooltipContent>
					</Portal>
				</Root>
			</Provider>
		)
	}
)

Tooltip.displayName = 'Tooltip'
